import { css } from '@inter-ikea-kompis/web-component';
import SheetAlignmentEnum from '../../enums/SheetAlignmentEnum.js';
import SheetAnimationNameEnum from '../../enums/SheetAnimationNameEnum.js';
import SheetSizeEnum from '../../enums/SheetSizeEnum.js';
const MIN_WIDTH = 260;
const SHEET_WIDTH_SIZE_LARGE = 1080;
const SHEET_WIDTH_SIZE_MEDIUM = 720;
const SHEET_WIDTH_SIZE_SMALL = 480;
const SHEET_MOBILE_MAX_WIDTH = 480;
const SHEET_DESKTOP_MIN_WIDTH = 481;
export default class SheetStyle {
    /**
     * Returns styles.
     *
     * @param options Options object.
     * @param options.top Offset from top.
     * @param options.bottom Offset from bottom.
     * @param options.theme Theme.
     * @param options.zIndex Z-index.
     * @param options.preserveAlignment ...
     * @param options.sheetSize ...
     * @param options.sheetAlignment ...
     * @param options.visible Visible sheet.
     * @param options.direction Safari write direction.
     * @returns Styles.
     */
    static getStyle(options) {
        const { color, motion } = options.theme;
        return css `
			:host {
				pointer-events: none;
				position: fixed;
				left: 0;
				right: 0;
				top: 0;
				bottom: 0;
				z-index: ${options.zIndex};
			}
			* {
				z-index: inherit;
			}
			kompis-sheet-backdrop {
				pointer-events: auto;
			}
			.container {
				position: absolute;
				left: 0;
				right: 0;
				top: 0;
				bottom: 0;
				display: flex;
				pointer-events: none;
				transition:
					top ${motion.duration.medium} ${motion.ease.easy},
					bottom ${motion.duration.medium} ${motion.ease.easy};
			}
			.sheet {
				pointer-events: ${options.isAnimating ? 'none' : 'auto'};
				overflow: hidden;
				position: relative;
				background-color: ${color.neutral.white};
				display: flex;
				transition-property: right, bottom;
				max-width: 100%;
				flex-direction: column;
				${this.getAnimationName(options)};
				animation-duration: ${options.visible ? motion.duration.medium : motion.duration.small};
				animation-fill-mode: both;
				animation-timing-function: ${options.visible ? motion.ease.enter : motion.ease.exit};
				outline: none;
			}
			.content {
				display: flex;
				flex-grow: 1;
				flex-direction: column;
				overflow: hidden;
				min-width: ${MIN_WIDTH / 16}rem;
			}
			${this.getResponsiveRules(options)}
		`;
    }
    static getAnimationName(options) {
        if (!options.isAnimating) {
            return '';
        }
        if (options.visible) {
            return css `
				animation-name: ${SheetAnimationNameEnum.opening};
			`;
        }
        return css `
			animation-name: ${SheetAnimationNameEnum.closing};
		`;
    }
    /**
     * Gets the responsive rules for borders, opening, closing for sheet.
     *
     * @param options Options.
     */
    static getResponsiveRules(options) {
        if (options.preserveAlignment) {
            return this.getDesktopRules(options);
        }
        return css `
			@media screen and (max-width: ${SHEET_MOBILE_MAX_WIDTH / 16}rem) {
				${this.getMobileRules(options)};
			}
			@media screen and (min-width: ${SHEET_DESKTOP_MIN_WIDTH / 16}rem) {
				${this.getDesktopRules(options)};
			}
		`;
    }
    /**
     * Gets the mobile rules for borders, opening, closing for sheet.
     *
     * @param options Options.
     */
    static getMobileRules(options) {
        const { border, spacing } = options.theme;
        return css `
			.container {
				margin-top: ${spacing.space150 / 16}rem;
			}
			.sheet {
				flex-grow: 1;
				border-top-left-radius: ${border.radius.radius8 / 16}rem;
				border-top-right-radius: ${border.radius.radius8 / 16}rem;
			}
			@keyframes ${SheetAnimationNameEnum.opening} {
				from {
					bottom: -100%;
				}
				to {
					bottom: 0;
				}
			}
			@keyframes ${SheetAnimationNameEnum.closing} {
				from {
					bottom: 0;
				}
				to {
					bottom: -100%;
				}
			}
		`;
    }
    /**
     * Gets the desktop rules for borders, opening, closing for sheet.
     *
     * @param options Options.
     */
    static getDesktopRules(options) {
        const { border, spacing } = options.theme;
        const { radius } = border;
        const inverseAlignment = (options.direction === 'rtl') !== (options.sheetAlignment === SheetAlignmentEnum.right);
        const position = inverseAlignment ? '-100%' : '100%';
        return css `
			.sheet {
				width: ${this.getMaxWidth(options)};
				border-top-left-radius: ${inverseAlignment ? radius.radius8 / 16 + 'rem' : 0};
				border-bottom-left-radius: ${inverseAlignment ? radius.radius8 / 16 + 'rem' : 0};
				border-top-right-radius: ${!inverseAlignment ? radius.radius8 / 16 + 'rem' : 0};
				border-bottom-right-radius: ${!inverseAlignment ? radius.radius8 / 16 + 'rem' : 0};
			}
			.space {
				flex-shrink: 0;
				pointer-events: none;
				width: ${spacing.space300 / 16}rem;
			}
			.container {
				flex-direction: ${options.sheetAlignment === SheetAlignmentEnum.right
            ? 'row-reverse'
            : 'row'};
			}

			@keyframes ${SheetAnimationNameEnum.opening} {
				from {
					right: ${position};
				}
				to {
					right: 0;
				}
			}
			@keyframes ${SheetAnimationNameEnum.closing} {
				from {
					right: 0;
				}
				to {
					right: ${position};
				}
			}
		`;
    }
    /**
     * Returns max width.
     *
     * @param options Options.
     */
    static getMaxWidth(options) {
        switch (options.sheetSize) {
            case SheetSizeEnum.small:
                return `${SHEET_WIDTH_SIZE_SMALL / 16}rem`;
            case SheetSizeEnum.medium:
                return `${SHEET_WIDTH_SIZE_MEDIUM / 16}rem`;
            case SheetSizeEnum.large:
                return `${SHEET_WIDTH_SIZE_LARGE / 16}rem`;
            default:
                return '100%';
        }
    }
}
//# sourceMappingURL=SheetStyle.js.map